'use client'

import React from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import type { BlogCategory, PublicBlogPostListItem } from '@kiao-voice/api-contract/blog'
import { BlogCoverFrame, BlogCoverImage } from './blog-image'
import { blogCategoryLabel, blogDateAttribute, formatBlogDate } from './format'

/**
 * One article in a list. The SAME component backs the index list and the
 * article page's related posts — two copies would be two rows that slowly stop
 * agreeing on which date they print.
 *
 * A client component because it needs `t()` for the reading-time label, and the
 * article page (a server component) renders it for the related list.
 */

/**
 * Six framings of the one ink plate, standing in for per-article artwork until
 * an article carries a cover of its own. Values from `css/kiao-blog.css`
 * `.th-1`–`.th-6`; a coverless list would otherwise print the same picture six
 * times down the page.
 */
const INK_FRAMINGS = [
  { backgroundPosition: '0% 100%', backgroundSize: '300% auto' },
  { backgroundPosition: '33% 91%', backgroundSize: '300% auto' },
  { backgroundPosition: '63% 61%', backgroundSize: '300% auto' },
  { backgroundPosition: '75% 51%', backgroundSize: '300% auto' },
  { backgroundPosition: '18% 100%', backgroundSize: '220% auto' },
  { backgroundPosition: '41% 70%', backgroundSize: '380% auto' },
] as const

/**
 * Stable per-slug framing: the same article keeps the same picture across
 * renders and across pages, and neighbouring articles differ.
 */
export function inkFramingIndex(slug: string): number {
  let hash = 0
  for (const character of slug) hash = (hash * 31 + character.codePointAt(0)!) % 100_003
  return hash % INK_FRAMINGS.length
}

function InkThumb({ slug }: { slug: string }) {
  return (
    <span
      aria-hidden="true"
      className="absolute inset-0 bg-no-repeat [filter:grayscale(1)_contrast(1.04)] [mix-blend-mode:multiply]"
      style={{
        backgroundImage: "url('/campaign/ink-wave.webp')",
        ...INK_FRAMINGS[inkFramingIndex(slug)],
      }}
    />
  )
}

interface PostRowProps {
  post: PublicBlogPostListItem
  /**
   * The label source for this row's keys. Passed in rather than fetched: the
   * row renders inside two different payloads (the index and an article's
   * related strip) and each one already carries the labels for its own page.
   */
  categories: BlogCategory[]
  /**
   * Kept in the DOM but not shown — every article's link ships in the first
   * response so crawlers and「載入更多」see the same list.
   */
  hidden?: boolean
}

export function PostRow({ post, categories, hidden }: PostRowProps) {
  const { t } = useTranslation('web')
  return (
    <Link
      href={`/blog/${post.slug}`}
      hidden={hidden}
      className="grid grid-cols-[148px_1fr] gap-5 border-t border-[var(--line-soft)] py-[18px] pr-[10px] transition-colors [&:hover_h3]:underline [&:hover_h3]:decoration-1 [&:hover_h3]:underline-offset-4 hover:bg-[var(--fg-soft)] focus-visible:rounded-[4px] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--fg)] max-[620px]:grid-cols-[96px_1fr] max-[620px]:gap-[14px]"
    >
      <BlogCoverFrame className="rounded-[6px] border border-[var(--line-soft)] bg-[var(--bg)]">
        {post.coverImage ? (
          // The two grid track widths this thumbnail is laid out at; the
          // browser multiplies by the display's density from there.
          <BlogCoverImage
            width={post.coverImage.width}
            height={post.coverImage.height}
            src={post.coverImage.url}
            alt={post.coverImage.alt}
            sizes="(max-width: 620px) 96px, 148px"
          />
        ) : (
          <InkThumb slug={post.slug} />
        )}
      </BlogCoverFrame>

      <span className="block min-w-0">
        <span className="block text-[length:var(--text-base)] font-semibold text-[var(--brand)]">
          {post.categories.map((category, index) => (
            <React.Fragment key={category}>
              {index > 0 && (
                // The separator is punctuation, not a category — red is reserved
                // for the category names themselves.
                <span aria-hidden="true" className="mx-[5px] font-normal text-[var(--border)]">
                  ·
                </span>
              )}
              {blogCategoryLabel(categories, category)}
            </React.Fragment>
          ))}
        </span>
        <h3 className="mt-[5px] text-[length:var(--fs-card-title)] leading-[1.45] font-semibold">
          {post.title}
        </h3>
        {post.dek ? (
          <span className="mt-1.5 block text-[length:var(--fs-card-dek)] leading-[1.7] text-[var(--muted)] max-[620px]:hidden">
            {post.dek}
          </span>
        ) : null}
        <span className="meta mt-[9px] flex items-center gap-2.5">
          <time className="num" dateTime={blogDateAttribute(post.updatedAt)}>
            {formatBlogDate(post.updatedAt)}
          </time>
          <span aria-hidden="true" className="text-[var(--border)]">
            ·
          </span>
          <span>{t('blog.readingMinutes', { minutes: post.readingMinutes })}</span>
        </span>
      </span>
    </Link>
  )
}
